import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarPicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  createTheme,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
  useTheme,
} from '@mui/material/styles';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import Close from '@mui/icons-material/Close';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { i18n } from '@lingui/core';
import {
  DATE_FORMAT_SHORT,
  formatDateTime,
  getDateLanguage,
} from '../../lib/Date';
import { stopEffects } from '../../lib/Event';
import { useI18n } from '../../lib/hook/useI18n';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
  containerFixedWidth: {
    width: theme.spacing(9.5),
  },
  chip: {
    fontWeight: 'bold',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  chipBase: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  chipSecondary: {
    border: 'none',
    color: theme.palette.secondary.main,
  },
  chipClickable: {
    '&:hover': {
      color: 'white',
      backgroundColor: 'var(--chip-hover)',
    },
  },
  chipDisabled: {
    pointerEvents: 'none',
  },
}));

type ClassKey = 'chip';

export interface InlineDatePickerProps {
  classes?: Partial<Record<ClassKey, string>>;
  disabled?: boolean;
  hideOwnerAndDue?: boolean;
  onChange: (date: Date | null) => void;
  secondary?: boolean;
  tooltipTitle?: string;
  value: Date | null;
}

const InlineDatePicker: React.FC<InlineDatePickerProps> = (
  props: InlineDatePickerProps
): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();
  const theme = useTheme();

  const {
    classes: classesProp,
    disabled = false,
    hideOwnerAndDue = false,
    onChange,
    secondary = false,
    tooltipTitle,
    value,
  } = props;

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    if (disabled) {
      return;
    }

    setOpen(!open);
  };

  const handleCalendarChange = (date: Date | null) => {
    onChange(date);
    toggleOpen();
  };

  const onHover = () => {
    setHover(true);
  };

  const onHoverExit = () => {
    setHover(false);
  };

  /**
   * If a null value was given, we default to the current day because
   * the mui-pickers calendar component would throw an error. At least, this
   * way, we don't force parent components to default to new Date() in their states
   */
  const calendarValue = value || new Date();

  let component = (
    <IconButton color="inherit" disabled={disabled}>
      <CalendarTodayOutlined fontSize="small" />
    </IconButton>
  );

  if (!disabled && tooltipTitle) {
    component = (
      <Tooltip
        placement="top"
        PopperProps={{ disablePortal: true }}
        title={tooltipTitle}
      >
        <Box>{component}</Box>
      </Tooltip>
    );
  }

  if (value && hover && !disabled) {
    component = (
      <Chip
        classes={{
          root: classes.chip,
          clickable: classes.chipClickable,
        }}
        clickable
        label={t('map.task.date.edit')}
      />
    );
  } else if (value) {
    component = (
      <Chip
        classes={{
          root: classNames(
            classes.chip,
            classesProp?.chip || {
              [classes.chipBase]: true,
              [classes.chipSecondary]: secondary,
              [classes.chipDisabled]: disabled,
            }
          ),
        }}
        clickable
        label={formatDateTime(
          +value / 1000,
          DATE_FORMAT_SHORT,
          i18n.locale
        ).toUpperCase()}
        variant="outlined"
      />
    );
  }

  return (
    <>
      {!hideOwnerAndDue && (
        <StyledEngineProvider injectFirst>
          <ThemeProvider
            theme={createTheme(theme, {
              palette: {
                primary: theme.palette.secondary,
                secondary: theme.palette.secondary,
              },
            })}
          >
            <Box
              {...{ ref }}
              className={classNames(classes.container, {
                [classes.containerFixedWidth]: !!value,
              })}
              onClick={stopEffects(toggleOpen)}
              onMouseEnter={onHover}
              onMouseLeave={onHoverExit}
            >
              {component}
            </Box>

            <Popover
              anchorEl={ref?.current}
              disablePortal
              onClick={stopEffects()}
              onClose={toggleOpen}
              open={open}
            >
              <Box pb={1} px={1}>
                {value && (
                  <Box display="flex" justifyContent="flex-end" py={1}>
                    <Button
                      color="inherit"
                      onClick={() => handleCalendarChange(null)}
                      startIcon={<Close color="inherit" fontSize="small" />}
                      style={{ textTransform: 'none' }}
                    >
                      <Typography color="textSecondary" variant="body2">
                        {t('date_selector.clear')}
                      </Typography>
                    </Button>
                  </Box>
                )}
                <LocalizationProvider
                  adapterLocale={getDateLanguage(i18n.locale)}
                  dateAdapter={AdapterDateFns}
                >
                  <CalendarPicker
                    date={calendarValue}
                    onChange={handleCalendarChange}
                  />
                </LocalizationProvider>
              </Box>
            </Popover>
          </ThemeProvider>
        </StyledEngineProvider>
      )}
    </>
  );
};

export default InlineDatePicker;
